import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { select, Store } from '@ngrx/store';
import {
  getAssignedRoles,
  getDepartmentsColors,
  getFilters,
  getIsLoading,
  getTaskStatuses,
  selectNewTasks,
  selectReviewedTasks,
} from '../../store/tasks.selectors';
import { Filter, TaskFilter } from '../../store/tasks.reducer';
import {
  fetchTasks,
  fetchTaskStatuses,
  markTasksAsReviewed,
  removeAllFilters,
  removeTaskFilter,
  resetTasksStore,
} from '../../store/tasks.actions';
import { StatusDto, Task } from 'projects/shared/src/lib/core/interfaces';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute, Router } from '@angular/router';

@UntilDestroy()
@Component({
  selector: 'app-tasks',
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss'],
  standalone: false,
})
export class TasksComponent implements OnInit, OnDestroy {
  @Input() showHeader = true;
  @Input() isClickable: boolean = true;
  taskStatuses$!: Observable<StatusDto[] | undefined>;
  newTasks$!: Observable<Task[]>;
  reviewedTasks$!: Observable<Task[]>;
  assignedRoles$!: Observable<ReturnType<typeof getAssignedRoles>>;
  filters$!: Observable<Filter[]>;
  departmentsColors$!: Observable<Record<string, string>>;
  isLoading$!: Observable<boolean>;
  controlId: string = '';

  constructor(
    private store: Store,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fetchTaskStatuses({}));
    this.taskStatuses$ = this.store.select(getTaskStatuses);
    this.newTasks$ = this.store.pipe(select(selectNewTasks));
    this.reviewedTasks$ = this.store.pipe(select(selectReviewedTasks));
    this.activatedRoute.queryParams
      .pipe(
        untilDestroyed(this),
        tap((params) => {
          this.controlId = JSON.parse(params['selectedControl'] || '{}')?.id;
          this.store.dispatch(fetchTasks({ controlId: this.controlId }));
        }),
      )
      .subscribe();

    this.filters$ = this.store.pipe(select(getFilters));
    this.isLoading$ = this.store.pipe(select(getIsLoading));
    this.assignedRoles$ = this.store.pipe(select(getAssignedRoles));
    this.departmentsColors$ = this.store.pipe(select(getDepartmentsColors));
  }

  ngOnDestroy(): void {
    this.store.dispatch(resetTasksStore());
  }

  removeFilter(filterType: keyof TaskFilter, value: any): void {
    if (filterType === 'controlId') {
      this.router.navigate([], {
        queryParams: {},
        relativeTo: this.activatedRoute,
      });
    }
    this.store.dispatch(removeTaskFilter({ filterType: filterType, value }));
  }

  removeAllFilters(): void {
    if (this.controlId) {
      this.router.navigate([], {
        queryParams: {},
        relativeTo: this.activatedRoute,
      });
    }

    this.store.dispatch(removeAllFilters());
  }

  markTasksAsReviewed(controlId?: string) {
    this.store.dispatch(markTasksAsReviewed({ controlId }));
  }

  trackByFn(index: number, item: Task) {
    return item.id;
  }

  protected readonly Object = Object;
}
